// Multiplayer state and logic on protocol v2 (.ai/multiplayer-protocol-v2.md; steps 1 + 2 of .ai/multiplayer-plan.md,
// 2026-10-29): the lobby (Quick play, the waiting players, a friend's table), the countdown, the running race, "I'm
// done", the result with Rematch / New opponent / Lobby. The fixed rooms of v1 are gone from the new UI (the legacy UI
// keeps them). The windows (MultiplayerWindows.tsx, MultiplayerLobby.tsx) and the opponent bar render this store;
// app-entry wires it to the engine through the hooks.
import { useSyncExternalStore } from 'react';
import { get } from '../api/client';
import { storedCredentials } from '../account/player';
import { env } from '../env';
import { t, currentLanguage } from '../i18n';
import { reportWinLine } from '../ui/playReport';
import { playSound } from '../sound/sound';
import { uiStore } from '../store/uiStore';
import { Mp2Client, type Mp2CatalogGame, type Mp2Frame, type Mp2Player, type Mp2Table } from './client2';
import type { CardEndFrame, CardStart, CardStateFrame } from '../online';
import { CARD_WINDOWS } from '../online/common';

export interface MpPlayerRef {
  id: number | string;
  name: string;
  country_code?: string | undefined;
}

export interface Opponent {
  name: string;
  country_code?: string | undefined;
  score: number;
  moves: number;
  /** Pressed "I'm done": the score is final. */
  done?: boolean;
  /** Connection lost, the seat kept (the server waits 30 s). */
  away?: boolean;
}

export interface MpResultPlayer {
  /** This player is me (the result window says "You win" and marks my side). */
  me?: boolean;
  name: string;
  country_code?: string | undefined;
  moves: number;
  score: number;
  time: number;
}

export interface MpResult {
  game_type: string;
  score_type: string;
  time: number;
  players: [MpResultPlayer, MpResultPlayer];
  /** The opponent left the running game: their name (the one who stayed wins). */
  left?: string;
  /** The server's winner (index into players; null = a draw). Without it the window decides by score, then moves. */
  winner?: number | null;
}

export interface LobbyState {
  /** The open tables I could join: the first player's name and flag (the race), and everyone seated (card games). */
  tables: {
    id: string;
    game: string;
    name: string;
    flag?: string | undefined;
    variants: string[];
    since: number;
    players: { name: string; flag?: string | undefined }[];
    seats: number;
  }[];
  online: number;
  playing: number;
  /** Games in progress per variant (`klondike:turn-one` -> 3). */
  games: Record<string, number>;
  /** The public games in progress, newest first (owner 2026-10-30: who plays whom, who leads, the points and the time):
   *  the seats (a bot `{bot: true}`), each seat's points (or cards left in Makao: `unit`) and the leaders' seats. */
  running: RunningGame[];
  /** A table of this account waiting on another device (one account, one seat): the page says so, it cannot join it. */
  mineElsewhere: string | null;
  today: number;
}

/** A private game shows only that it runs: no seats, no numbers. */
export interface RunningGame {
  id: string;
  game: string;
  variant: string;
  since: number;
  private?: boolean;
  seats?: Array<{ name: string; flag?: string } | { bot: true }>;
  unit?: 'points' | 'cards';
  values?: number[];
  leaders?: number[];
  /** A bot's seat a newcomer may take (a public card game; owner 2026-11-01). */
  join?: boolean;
}

export interface MpState {
  connected: boolean;
  /** off: never opened or closed; connecting; ready (welcome came); lost (trying again). */
  status: 'off' | 'connecting' | 'ready' | 'lost';
  me: Mp2Player | null;
  catalog: Mp2CatalogGame[];
  lobby: LobbyState | null;
  /** My table (waiting, countdown, playing or ended), null when I sit nowhere. */
  table: Mp2Table | null;
  /** Quick play or an open table of mine waits for an opponent: the bar over the board. */
  searching: { game: string; since: number } | null;
  /** My waiting room closed after 30 minutes with no change: the bar says so until it is dismissed. */
  searchClosed: boolean;
  /** The last refusal of the server shown in the lobby (a wrong code, a table already full). */
  error: string | null;
  countdown: { sec: number; opponent: MpPlayerRef | null; game: string; minutes: number } | null;
  gameStarted: boolean;
  opponent: Opponent | null;
  result: MpResult | null;
  /** I pressed "I'm done", or the page proved my game lost (`stuck`): my final score, while the opponent still plays. */
  meDone: { score: number; moves: number; time: number; stuck: boolean } | null;
  /** When the time limit ends (Date.now() ms), for the "time left" of the waiting window; null when no game runs. */
  endsAt: number | null;
  /** After the end: I asked for a rematch / the opponent did. */
  rematch: { me: boolean; them: boolean; gone: boolean };
  /** A card game (Makao, Kierki) of mine runs or just ended: its id (the table shows it; the Menu button leaves). */
  cards: { game: string } | null;
  /** The last thing that happened to a seat ("Anna left the table, a bot plays for them"), shown a few seconds. */
  cardNotice: { text: string; name: string; flag?: string | undefined; n: number } | null;
  /** I took a bot's seat in a game in progress: the bot plays it until the next deal, my tap does nothing. */
  watching: boolean;
  /** My turns ran out several times: the bot plays for me until I press "I'm back". */
  asleep: boolean;
  /** The quick messages of a game's end, the last of each seat (the race: seat of the table; card tables: 0 = me). */
  said: Record<number, Said>;
}

/** A quick message from a player at the end of a game; `n` tells each one from the one before. */
export interface Said {
  say: string;
  /** For a screen reader only: the bubble comes out of the sender's side. */
  name: string;
  n: number;
}

export interface MpHooks {
  startGame(gameName: string, pattern: number[]): void;
  finishGame(): void;
  cancelGame(): void;
  /** "I'm done": the clock stops, the board waits under the waiting window. */
  doneGame(): void;
  /** A card game: its online table takes the place of the offline one (online/), then its frames, then back. */
  startCards(start: CardStart): void;
  cardState(f: CardStateFrame): void;
  /** A seat changed hands (`game.seat`): `bot` while a bot plays it. */
  cardSeat(seat: number, name: string, bot: boolean, flag?: string): void;
  endCards(f: CardEndFrame): void;
  stopCards(): void;
}

/** After "Lobby" in the result window the page reloads (the ads refresh, owner 2026-10-29) and opens the lobby again. */
const OPEN_LOBBY_KEY = 'po-mp-open-lobby';
/** The win line named a waiting player: their table, to sit at after the reload. */
const JOIN_TABLE_KEY = 'po-mp-join-table';
/** The Win window's reload while my room waits: when it happened, so the new page takes the seat back (server: table.hold). */
const KEEP_WAITING_KEY = 'po-mp-keep-waiting';
const KEEP_WAITING_MS = 30_000; // the server's AWAY_MS
const HOST_AWAY_RETRY_MS = 5000;
/** `?src=` of a tagged link (an ad, a post), kept for the tab's session; said in hello, for /admin's "where from". */
const SOURCE_KEY = 'po-mp-src';
const taggedSource = (): string | null => {
  try {
    const src = new URLSearchParams(location.search).get('src');
    if (src && /^[A-Za-z0-9_-]{1,24}$/.test(src)) sessionStorage.setItem(SOURCE_KEY, src);
    return sessionStorage.getItem(SOURCE_KEY);
  } catch {
    return null;
  }
};

const toRef = (p: Mp2Player | null | undefined): MpPlayerRef | null =>
  p ? { id: p.id, name: p.name, country_code: p.flag || undefined } : null;

export function createMultiplayerStore(client: Mp2Client) {
  let state: MpState = {
    connected: false,
    status: 'off',
    me: null,
    catalog: [],
    lobby: null,
    table: null,
    searching: null,
    searchClosed: false,
    error: null,
    countdown: null,
    gameStarted: false,
    opponent: null,
    result: null,
    meDone: null,
    endsAt: null,
    cards: null,
    cardNotice: null,
    watching: false,
    asleep: false,
    said: {},
    rematch: { me: false, them: false, gone: false },
  };
  const listeners = new Set<() => void>();
  let hooks: MpHooks | null = null;
  let saidCount = 0;
  let countdownTimer: number | null = null;
  /** The tab's own title while the countdown shows its seconds there; every way out of the countdown puts it back. */
  let titleBefore: string | null = null;
  /** The table whose game this page has dealt (a resume of another one after a reload cannot be played on). */
  let dealtTable: string | null = null;
  /** The number of the last card-game state from the server (`game.state` v): my moves name it. */
  let cardsV: number | null = null;
  let myStats = { score: 0, moves: 0 };
  let startedAt = 0;
  let watching = false;
  /** A friend's code from the link (`?t=K7QX`), joined as soon as the server has welcomed us. */
  let pendingCode: string | null = null;
  /** The code I last tried to join with, while the table's host is away: asked again each HOST_AWAY_RETRY_MS. */
  let joiningCode: string | null = null;
  let joinRetry = 0;
  const stopJoinRetry = () => {
    joiningCode = null;
    clearTimeout(joinRetry);
  };
  let pendingTable: string | null = null;
  /** A table named on the win line may be gone: its `not-found` must not speak of a code. */
  let joiningNamed = false;
  const set = (patch: Partial<MpState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  /** My seat at the last table I sat at: the game.end of my own leave comes after I have let the table go. */
  let lastSeat = -1;
  /** I left a running game myself: its game.end (reason left) brings no result window. */
  let leftByMe = false;
  const mySeat = (tb: Mp2Table | null = state.table): number =>
    tb && state.me ? tb.seats.findIndex((p) => p && String(p.id) === String(state.me!.id)) : lastSeat;
  const otherOf = (tb: Mp2Table | null): Mp2Player | null => {
    const i = mySeat(tb);
    return tb && i >= 0 ? (tb.seats[1 - i] ?? null) : null;
  };
  /** A card game (Makao, Kierki): turns, four seats, bots in the empty ones. */
  const isCards = (game: string) => state.catalog.find((g) => g.game === game)?.kind === 'cards';
  const minutesOf = (variant: string) =>
    state.catalog.flatMap((g) => g.variants).find((v) => v.id === variant)?.minutes ?? 0;

  const closeWindows = (...names: Parameters<ReturnType<typeof uiStore>['closeWindow']>[0][]) => {
    const ui = uiStore();
    for (const n of names) if (ui.isOpen(n)) ui.closeWindow(n);
  };
  const stopCountdown = () => {
    if (countdownTimer !== null) clearInterval(countdownTimer);
    countdownTimer = null;
    if (titleBefore !== null) document.title = titleBefore;
    titleBefore = null;
    if (state.countdown) set({ countdown: null });
  };
  const watchLobby = (on: boolean) => {
    if (on === watching) return;
    watching = on;
    client.send({ t: on ? 'lobby.watch' : 'lobby.unwatch' });
  };
  /** The lobby window in front (opened or restored). */
  const showLobby = () => {
    const ui = uiStore();
    const w = ui.getState().windows.find((x) => x.name === 'multiplayer' && !x.closing);
    if (w?.minimized) ui.restoreWindow('multiplayer');
    else if (!w) ui.openWindow('multiplayer');
    watchLobby(true);
  };
  const resetGame = () => {
    stopCountdown();
    set({ gameStarted: false, opponent: null, meDone: null, endsAt: null });
  };

  /** The page came back from the Win window's reload to take its waiting seat: nothing else is opened. */
  let resumingWait = false;

  // ---- frames ------------------------------------------------------------------------------------
  const onWelcome = (f: Mp2Frame) => {
    const me = f.me as Mp2Player;
    set({
      status: 'ready',
      connected: true,
      me,
      catalog: Array.isArray(f.catalog) ? (f.catalog as Mp2CatalogGame[]) : [],
    });
    const resume = (f.resume ?? null) as Mp2Table | null;
    if (resumingWait) {
      resumingWait = false;
      if (!resume || resume.state !== 'waiting') {
        // the seat is gone (the reload took too long, or another window has it): the page stays a solo game
        client.seated = false;
        client.close();
        set({ status: 'off', connected: false });
        return;
      }
    }
    if (resume) {
      // a game this page did not deal (a reload in the middle of it) cannot go on: give it up (the spec's rule)
      if (
        (resume.state === 'countdown' || resume.state === 'playing') &&
        dealtTable !== resume.id &&
        !isCards(resume.game)
      ) {
        client.send({ t: 'table.leave' });
        set({ table: null, searching: null });
      } else onTable({ t: 'table', table: resume });
    } else if (state.table) {
      // the seat was lost while away (more than 30 s): the server no longer knows the table
      const wasPlaying = state.gameStarted;
      set({ table: null, searching: null });
      if (wasPlaying) {
        resetGame();
        hooks?.cancelGame();
      }
    }
    if (uiStore().isOpen('multiplayer') || watching) {
      watching = false;
      watchLobby(true);
    }
    if (pendingCode) {
      joiningCode = pendingCode;
      client.send({ t: 'table.join', code: pendingCode });
      pendingCode = null;
    }
    if (pendingTable) {
      client.send({ t: 'table.join', id: pendingTable });
      pendingTable = null;
      joiningNamed = true;
    }
  };

  const onLobby = (f: Mp2Frame) => {
    const all = Array.isArray(f.tables) ? (f.tables as Mp2Table[]) : [];
    const mine = all.find((tb) => mySeat(tb) >= 0 && tb.id !== state.table?.id);
    const tables = all
      .filter((tb) => mySeat(tb) < 0)
      .map((tb) => {
        const p = tb.seats.find(Boolean)!;
        const players = tb.seats
          .filter((x): x is Mp2Player => !!x)
          .map((x) => ({ name: x.name, flag: x.flag || undefined }));
        return {
          id: tb.id,
          game: tb.game,
          name: p.name,
          flag: p.flag || undefined,
          variants: tb.variants,
          since: tb.since,
          players,
          seats: tb.seats.length,
        };
      });
    const games = f.games && typeof f.games === 'object' ? (f.games as Record<string, number>) : {};
    const running = (Array.isArray(f.running) ? (f.running as RunningGame[]) : []).filter(
      (g) => g.private || (Array.isArray(g.seats) && g.seats.length > 0),
    );
    set({
      lobby: {
        tables,
        online: Number(f.online ?? 0),
        playing: Number(f.playing ?? 0),
        games,
        running,
        mineElsewhere: mine?.variants[0] ?? null,
        today: Number(f.today ?? 0),
      },
    });
  };

  const onTable = (f: Mp2Frame) => {
    const tb = (f.table ?? null) as Mp2Table | null;
    const before = state.table;
    if (!tb) {
      // I sit nowhere now (I left; a public table closes when the other one leaves after a game; idle clean-up)
      client.seated = false;
      // idle clean-up: under the bar, the bar says so; in the lobby window (a card table, a friend's), its red line does
      const idle = f.why === 'idle';
      const searchClosed = idle && !!state.searching;
      set({
        table: null,
        searching: null,
        searchClosed,
        rematch: { ...state.rematch, gone: true },
        ...(idle && !searchClosed ? { error: t('lobby.idle-closed') } : {}),
      });
      stopCards();
      if (!uiStore().isOpen('multiplayer')) watchLobby(false);
      return;
    }
    stopJoinRetry(); // seated: no more asking
    const other = otherOf(tb);
    lastSeat = mySeat(tb);
    client.seated = lastSeat >= 0;
    const patch: Partial<MpState> = { table: tb, error: null };
    const cards = isCards(tb.game);
    // "we will tell you when someone joins" (the waiter may be in another tab): a race starts at once, a card table fills
    const raceJoined = state.searching && tb.state === 'countdown';
    const cardJoined = cards && tb.state === 'waiting' && before?.id === tb.id && tb.seats.filter(Boolean).length > before.seats.filter(Boolean).length;
    if (raceJoined || cardJoined) playSound('deal');
    if (tb.state === 'waiting') {
      // a card table waits in the lobby window (the waiting room with "Start with bots"), not under the bar
      patch.searching = !tb.private && !cards ? { game: tb.game, since: tb.since } : null;
      if (before?.state === 'ended' && !other) patch.rematch = { ...state.rematch, gone: true };
    } else patch.searching = null;
    if (tb.state === 'ended') {
      const i = mySeat(tb);
      patch.rematch = { me: tb.rematch.includes(i), them: tb.rematch.includes(1 - i), gone: !other };
    }
    if (state.gameStarted && other)
      patch.opponent = {
        ...(state.opponent ?? { name: other.name, score: 0, moves: 0 }),
        name: other.name,
        country_code: other.flag || undefined,
        away: !!other.away,
      };
    set(patch);
    // a public table that waits: the lobby gets out of the way, the player goes on with a solo game under the bar
    if (tb.state === 'waiting' && !tb.private && !cards && uiStore().isOpen('multiplayer')) {
      uiStore().minimizeWindow('multiplayer');
    }
    // the bar names who else waits (owner 2026-11-01, pick B of branch prototype/mp-next-2026-11-01): a waiter stays a
    // watcher of the lobby until the game starts or the table goes
    if (tb.state === 'waiting' && !tb.private && !cards) watchLobby(true);
  };

  // ---- card games (online/, .ai/multiplayer-protocol-v2.md "Card games") ------------------------------------
  /** My card game ended or I left its table: the offline table comes back. */
  const stopCards = () => {
    if (!state.cards) return;
    set({ cards: null, cardNotice: null, asleep: false, watching: false });
    closeWindows('multiplayer_leave');
    hooks?.stopCards();
  };
  const onCardStart = (f: Mp2Frame) => {
    const start = f as unknown as CardStart;
    closeWindows('multiplayer', 'multiplayer_leave');
    watchLobby(false);
    set({ cards: { game: start.game }, searching: null, error: null });
    hooks?.startCards(start);
  };

  const onGameStart = (f: Mp2Frame) => {
    reportWinLine('games');
    set({ said: {} });
    if (f.kind === 'cards') return onCardStart(f);
    const tb = state.table;
    const variant = String(f.variant ?? '');
    const pattern = Array.isArray(f.pattern) ? (f.pattern as number[]) : [];
    const limit = Number(f.limit ?? 0);
    const wait = Math.max(1, Number(f.countdown ?? 10));
    const opponent = toRef(otherOf(tb));
    closeWindows('multiplayer_win', 'multiplayer_done', 'multiplayer_leave', 'no_moves');
    set({ result: null, searching: null, rematch: { me: false, them: false, gone: false } });
    watchLobby(false);
    showLobby(); // the countdown card is the lobby window's other face
    watchLobby(false);
    const title = (sec: number) => t('multiplayer.start-game-countdown-title', { sec });
    const info = { opponent, game: variant, minutes: Math.round(limit / 60) || minutesOf(variant) };
    stopCountdown();
    titleBefore = document.title;
    let sec = wait;
    set({ countdown: { sec, ...info } });
    countdownTimer = window.setInterval(() => {
      sec -= 1;
      document.title = title(sec);
      if (sec > 0) {
        set({ countdown: { sec, ...info } });
        return;
      }
      stopCountdown();
      uiStore().minimizeWindow('multiplayer');
      myStats = { score: 0, moves: 0 };
      startedAt = Date.now();
      dealtTable = state.table?.id ?? null;
      set({
        gameStarted: true,
        meDone: null,
        endsAt: startedAt + limit * 1000,
        opponent: { name: opponent?.name ?? '...', country_code: opponent?.country_code, score: 0, moves: 0 },
      });
      hooks?.startGame(variant, pattern);
    }, 1000);
  };

  const onProgress = (f: Mp2Frame) => {
    if (!state.gameStarted || !state.opponent || state.opponent.done) return;
    if (Number(f.seat) === mySeat()) return;
    set({
      opponent: { ...state.opponent, score: Number(f.score ?? 0), moves: Number(f.moves ?? 0), done: f.done === true },
    });
  };

  const onGameEnd = (f: Mp2Frame) => {
    set({ said: {} });
    // a card game: its end window; the table waits again (Play again = Start, or leave)
    if (f.kind === 'cards') return hooks?.endCards(f as unknown as CardEndFrame);
    const players = (Array.isArray(f.players) ? f.players : []) as Array<Record<string, unknown>>;
    const i = mySeat();
    const toPlayer = (p: Record<string, unknown> | undefined, idx: number): MpResultPlayer => ({
      me: idx === i,
      name: String(p?.name ?? '???'),
      country_code: (p?.flag as string) || undefined,
      score: Number(p?.score ?? 0),
      moves: Number(p?.moves ?? 0),
      time: Number(p?.time ?? 0),
    });
    const reason = String(f.reason ?? '');
    const winner = f.winner === null ? null : Number(f.winner);
    const leftIdx = players.findIndex((p) => p.left === true);
    const result: MpResult = {
      game_type: String(f.variant ?? state.table?.variant ?? ''),
      score_type: 'standard',
      time: Number((players[0]?.time as number) ?? 0),
      players: [toPlayer(players[0], 0), toPlayer(players[1], 1)],
      winner,
      ...(reason === 'left' && leftIdx >= 0 && leftIdx !== i ? { left: String(players[leftIdx]!.name ?? '') } : {}),
    };
    const wasStarted = state.gameStarted;
    closeWindows('multiplayer_done', 'multiplayer_leave', 'no_moves');
    stopCountdown();
    set({
      result,
      gameStarted: false,
      opponent: null,
      meDone: null,
      endsAt: null,
      rematch: { me: false, them: false, gone: reason === 'left' },
    });
    if (leftByMe || (reason === 'left' && leftIdx === i)) {
      leftByMe = false;
      // my own leave: no result for me, back to the lobby
      set({ result: null });
      showLobby();
    } else uiStore().openWindow('multiplayer_win', { v2: true });
    if (wasStarted) hooks?.finishGame();
    else hooks?.cancelGame();
  };

  const onError = (f: Mp2Frame) => {
    const raw = String(f.code ?? '');
    const code = joiningNamed && raw === 'not-found' ? 'not-waiting' : raw;
    joiningNamed = false;
    if (code === 'host-away' && joiningCode) {
      const again = joiningCode;
      set({ error: t('lobby.error.host-away') });
      clearTimeout(joinRetry);
      joinRetry = window.setTimeout(() => {
        if (joiningCode === again) client.send({ t: 'table.join', code: again });
      }, HOST_AWAY_RETRY_MS);
      return;
    }
    stopJoinRetry();
    if (
      code === 'not-found' ||
      code === 'full' ||
      code === 'not-waiting' ||
      code === 'too-many-tables' ||
      code === 'busy' ||
      code === 'seated' ||
      code === 'seat-moved'
    )
      set({ error: t(`lobby.error.${code}`) });
  };

  const onClosed = (f: Mp2Frame) => {
    if (state.status === 'off') return;
    if (f.clean === true) {
      set({ status: 'off', connected: false });
      return;
    }
    if (f.failed === true) {
      // the server cannot be reached, or came back no more: the legacy alert, the lobby closes
      const wasStarted = state.gameStarted;
      resetGame();
      set({ status: 'off', connected: false, table: null, searching: null, lobby: null });
      closeWindows('multiplayer', 'multiplayer_done', 'multiplayer_leave');
      stopCards();
      uiStore().openWindow('alert', { header: t('alert.header'), body: t('multiplayer.alert-disconnect') });
      if (wasStarted) hooks?.finishGame();
      return;
    }
    set({ status: 'lost', connected: false });
  };

  client.on((f) => {
    switch (f.t) {
      case 'welcome':
        return onWelcome(f);
      case 'lobby':
        return onLobby(f);
      case 'table':
        return onTable(f);
      case 'game.start':
        return onGameStart(f);
      case 'game.progress':
        return onProgress(f);
      case 'game.end':
        return onGameEnd(f);
      case 'game.seat': {
        // the others hear what happened to a seat; a bot plays it while its player is away or gone
        if (!state.cards) return undefined;
        const what = String(f.what ?? '');
        const name = String(f.name ?? '');
        const flag = typeof f.flag === 'string' ? f.flag : undefined;
        set({ cardNotice: { text: t(`lobby.seat.${what}`, { name }), name, flag, n: (state.cardNotice?.n ?? 0) + 1 } });
        return hooks?.cardSeat(Number(f.seat), name, what !== 'back' && what !== 'joins', flag);
      }
      case 'table.said': {
        const tb = state.table;
        if (!tb) return undefined;
        const seat = Number(f.seat);
        const mine = isCards(tb.game) ? (seat - mySeat(tb) + tb.seats.length) % tb.seats.length : seat;
        return set({
          said: { ...state.said, [mine]: { say: String(f.say ?? ''), name: String(f.name ?? ''), n: ++saidCount } },
        });
      }
      case 'game.asleep':
        return state.cards ? set({ asleep: true }) : undefined;
      case 'game.state':
        if (!state.cards) return undefined;
        if (state.asleep && typeof f.deadline === 'number') set({ asleep: false });
        if (state.watching !== (f.watching === true)) set({ watching: f.watching === true });
        cardsV = typeof f.v === 'number' ? f.v : null;
        return hooks?.cardState(f as unknown as CardStateFrame);
      case 'error':
        return onError(f);
      case 'closed':
        return onClosed(f);
      default:
        return undefined;
    }
  });

  const ensureConnected = () => {
    if (state.status === 'off') set({ status: 'connecting' });
    client.connect();
  };

  const api = {
    getState: () => state,
    subscribe(fn: () => void) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    setHooks(h: MpHooks) {
      hooks = h;
    },
    /** Menu > Multiplayer, /multiplayer.html, the reload after "Lobby": the lobby window (connect first). */
    open(opts: { code?: string; from?: string; table?: string } = {}) {
      const ui = uiStore();
      if (opts.code) pendingCode = opts.code.toUpperCase();
      if (opts.table) pendingTable = opts.table;
      set({ searchClosed: false });
      reportWinLine('lobby');
      if (!state.connected) client.source = (opts.from === 'win' ? 'win' : taggedSource()) ?? opts.from ?? (opts.code ? 'friend' : env.appTarget === 'multiplayer' ? 'page' : 'menu');
      if (typeof WebSocket !== 'function') {
        ui.openWindow('alert', { header: t('alert.header'), body: t('multiplayer.alert-not-supported') });
        return;
      }
      ensureConnected();
      showLobby();
    },
    /** The lobby window's close: stop watching; a table that waits for me is given up (I sit nowhere after it). */
    close() {
      stopJoinRetry();
      watchLobby(false);
      if (state.table && (state.table.state === 'waiting' || state.table.state === 'ended'))
        client.send({ t: 'table.leave' });
      if (!state.gameStarted && !state.countdown) {
        client.close();
        set({ status: 'off', connected: false, table: null, searching: null, lobby: null });
      }
    },
    /** The bar's "your room closed" is read. */
    dismissClosed() {
      set({ searchClosed: false });
    },
    /** The Win window's New game is about to reload the page (the ad refresh): my waiting room must survive it. */
    holdForReload() {
      if (!state.searching) return;
      client.send({ t: 'table.hold' });
      try {
        sessionStorage.setItem(KEEP_WAITING_KEY, String(Date.now()));
      } catch {
        /* no storage: the seat goes, as before */
      }
    },
    /** At start, after that reload: back to the waiting seat, the bar over the new solo game. True if it tries. */
    resumeWaiting(): boolean {
      try {
        const at = Number(sessionStorage.getItem(KEEP_WAITING_KEY));
        sessionStorage.removeItem(KEEP_WAITING_KEY);
        if (!(Date.now() - at < KEEP_WAITING_MS) || typeof WebSocket !== 'function') return false;
      } catch {
        return false;
      }
      resumingWait = true;
      client.seated = true;
      ensureConnected();
      return true;
    },
    quick(game: string, variants: string[]) {
      set({ error: null });
      client.send({ t: 'quick', game, variants });
    },
    joinTable(id: string) {
      set({ error: null });
      client.send({ t: 'table.join', id });
    },
    joinCode(code: string) {
      const c = code.trim().toUpperCase();
      if (c.length !== 4) return set({ error: t('lobby.error.not-found') });
      set({ error: null });
      joiningCode = c;
      client.send({ t: 'table.join', code: c });
    },
    /** The link a friend opens: the multiplayer page with the table's code; `g` and `l` give the message its preview. */
    friendLink: (code: string, variant?: string) =>
      `${env.fullAddr}multiplayer.html?t=${code}${variant ? `&g=${encodeURIComponent(variant)}` : ''}${currentLanguage() === 'pl-pl' ? '' : '&l=en'}`,
    /** A new public table of this variant (the lobby's white strip); with too many waiting the server seats me at one. */
    createRoom(game: string, variant: string) {
      set({ error: null });
      client.send({ t: 'table.create', game, variant, private: false });
    },
    friend(game: string, variant: string) {
      set({ error: null });
      client.send({ t: 'table.create', game, variant, private: true });
    },
    /** A card table: "Start with bots" (on) or take it back (off); the game starts when every seated player is ready. */
    ready(on: boolean) {
      client.send({ t: 'table.ready', on });
    },
    /** "I'm back" after the bot played for me. */
    back() {
      client.send({ t: 'table.back' });
      set({ asleep: false });
    },
    /** A card game's move (online/), with the number of the state it was made on (a late one is refused). */
    move(m: Record<string, unknown>) {
      if (state.watching) return;
      if (state.asleep) set({ asleep: false });
      client.send({ t: 'game.move', move: m, ...(cardsV !== null ? { v: cardsV } : {}) });
    },
    /** After a card game: Start again at the same table, the waiting room in front. */
    again() {
      closeWindows(...CARD_WINDOWS);
      client.send({ t: 'table.ready', on: true });
      showLobby();
    },
    /** Leave the card table (a bot takes my seat if the game runs): the offline table again, the lobby in front. */
    leaveCards() {
      client.send({ t: 'table.leave' });
      set({ table: null });
      stopCards();
      showLobby();
    },
    isCards,
    /** My seat at my table (-1 when none). */
    mySeat: () => mySeat(),
    /** The searching bar's Cancel, the friend's table's Cancel. */
    cancelTable() {
      client.send({ t: 'table.leave' });
      set({ table: null, searching: null });
    },
    /** Leave the game (the opponent wins) or a table. */
    leaveRoom() {
      const wasStarted = state.gameStarted;
      leftByMe = wasStarted || !!state.countdown;
      client.send({ t: 'table.leave' });
      closeWindows('multiplayer_done', 'multiplayer_leave');
      resetGame();
      set({ table: null, searching: null });
      if (wasStarted) hooks?.cancelGame();
      showLobby();
    },
    updateGameStats(stats: { score: number; moves: number }) {
      if (!state.gameStarted || state.meDone) return;
      myStats = { ...stats };
      client.send({ t: 'game.progress', ...stats });
    },
    /** The board was cleared: the finisher wins (protocol v2). */
    finishGame() {
      if (!state.gameStarted) return;
      client.send({ t: 'game.finish', ...myStats });
    },
    /** "I'm done": stop with my score now; the waiting window covers the board until the end (the server ends the
     *  game at once when the opponent has more points). `stuck`: no move leads to a win any more (ui/lostGame.ts),
     *  the same without asking (owner 2026-10-30). */
    done(stuck = false) {
      if (!state.gameStarted || state.meDone) return;
      client.send({ t: 'game.done', ...myStats });
      set({ meDone: { ...myStats, time: Math.max(0, Math.round((Date.now() - startedAt) / 1000)), stuck } });
      hooks?.doneGame();
      uiStore().openWindow('multiplayer_done');
    },
    /** A quick message at the end of a game; everyone at the table, me too, sees it when the server sends it back. */
    say(say: string) {
      client.send({ t: 'table.say', say });
    },
    rematch() {
      if (state.rematch.gone) return;
      client.send({ t: 'rematch' });
      set({ rematch: { ...state.rematch, me: true } });
    },
    /** New opponent: leave this table and Quick play the same game and variant again. */
    newOpponent() {
      const variant = state.result?.game_type || state.table?.variant || '';
      const game = variant.split(':')[0] ?? '';
      closeWindows('multiplayer_win');
      client.send({ t: 'table.leave' });
      set({ result: null, table: null });
      if (game) client.send({ t: 'quick', game, variants: [variant] });
    },
    /** Lobby: leave the table and reload the page (the ads refresh), the lobby opens again after it. */
    toLobby() {
      client.send({ t: 'table.leave' });
      try {
        sessionStorage.setItem(OPEN_LOBBY_KEY, '1');
      } catch {
        // no storage: the page comes back without the lobby
      }
      client.close();
      const target = `${env.fullAddr}#${location.hash.slice(1) || 'klondike'}`;
      if (location.href.split('#')[0] === target.split('#')[0]) location.reload();
      else location.href = target;
    },
    /** The Win window's "play with people": the same reload as its New game (the ads refresh), the lobby opens after it. */
    toLobbyFromWin(game: string, table?: string) {
      try {
        sessionStorage.setItem(OPEN_LOBBY_KEY, 'win');
        if (table) sessionStorage.setItem(JOIN_TABLE_KEY, table);
      } catch {
        // no storage: the page comes back without the lobby
      }
      client.close();
      const target = `${env.fullAddr}#${game}`;
      if (location.href.split('#')[0] === target.split('#')[0]) {
        location.hash = game;
        location.reload();
      } else location.href = target;
    },
    /** v1's Ok: the result window closes and the lobby comes back. */
    confirmResult() {
      set({ result: null });
      closeWindows('multiplayer_win');
      if (state.table) client.send({ t: 'table.leave' });
      set({ table: null });
      showLobby();
    },
    /** At start: the lobby again after "Lobby" reloaded the page. */
    reopenAfterReload(): boolean {
      let from: string | null;
      let table: string | null = null;
      try {
        from = sessionStorage.getItem(OPEN_LOBBY_KEY);
        if (from !== '1' && from !== 'win') return false;
        sessionStorage.removeItem(OPEN_LOBBY_KEY);
        table = sessionStorage.getItem(JOIN_TABLE_KEY);
        sessionStorage.removeItem(JOIN_TABLE_KEY);
      } catch {
        return false;
      }
      api.open({ from: from === 'win' ? 'win' : 'reload', ...(table ? { table } : {}) });
      return true;
    },
    isInGame: () => state.gameStarted,
    /** My score and moves as last reported, my name and flag (the scoreboards of the multiplayer windows). */
    myStats: () => ({ ...myStats }),
    myName: () => String(state.me?.name ?? ''),
    myFlag: () => state.me?.flag || undefined,
  };
  return api;
}

export type MultiplayerStore = ReturnType<typeof createMultiplayerStore>;

/** The page's own ticket for the server (logged-in players): the session, or the stored credentials as headers. */
export async function fetchTicket(): Promise<string | null> {
  if (!uiStore().getState().player) return null;
  const creds = storedCredentials();
  const r = await get('auth/mp-ticket.html', {}, creds ? { 'X-Po-Name': creds.name, 'X-Po-Hash': creds.hash } : {});
  const ticket = (r as unknown as { ticket?: unknown }).ticket;
  return r.result === 'ok' && typeof ticket === 'string' ? ticket : null;
}

/** A player's flag in the lobby: the PNG (their <img> has no size of its own; an SVG would come at its full 640 x 480).
 *  The card tables' seat badge and the race footer take the SVG (sharpFlag; online/SeatAvatar.tsx, OpponentBar). */
export const flagUrl = (code: string | null | undefined): string =>
  `${(globalThis as { SS?: string }).SS ?? '/'}images/flags/${code || '01'}.png`;

let current: MultiplayerStore | null = null;
export const setMultiplayerStore = (s: MultiplayerStore) => {
  current = s;
};
export const mpStore = (): MultiplayerStore => {
  if (!current) throw new Error('multiplayer store not created');
  return current;
};
export function useMultiplayerState(): MpState {
  const s = mpStore();
  return useSyncExternalStore(s.subscribe, s.getState, s.getState);
}
